import { useState, useEffect } from "react";
import { getRolesActivos } from "../../../services/usuarios_tienda/roles";
import "./UserFormModal.css";

function UserFormModal({ isOpen, onClose, userData, onSubmit }) {
  const isEdit = !!userData;
  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    correo: "",
    estado: 1,
    rol_tienda_id: "",
  });
  const [roles, setRoles] = useState([]);
  const [alert, setAlert] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setForm({
      nombre: userData?.nombre || "",
      apellido: userData?.apellido || "",
      correo: userData?.correo || "",
      estado: userData?.estado ?? 1,
      rol_tienda_id: userData?.rol_tienda_id || "",
    });

    setAlert(null);
  }, [isOpen, userData]);

  useEffect(() => {
    if (!isOpen) return;

    const loadRoles = async () => {
      try {
        const r = await getRolesActivos();
        setRoles(Array.isArray(r) ? r : []);
      } catch (err) {
        console.error("Error cargando roles:", err);
        setRoles([]);
        setAlert({ type: "error", message: getNiceErrorMessage(err, "No se pudieron cargar los roles.") });
      }
    };

    loadRoles();
  }, [isOpen]);

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const getNiceErrorMessage = (error, fallback = "La operación falló.") => {
    const data = error?.response?.data;
    const firstFieldError = Array.isArray(data?.errors) ? data.errors[0]?.msg : null;
    const msg =
      data?.message ||
      data?.error ||
      firstFieldError ||
      error?.message ||
      fallback;
    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 200 ? clean.slice(0, 200) + "…" : clean;
  };

  const submit = async (e) => {
    e.preventDefault();
    setAlert(null);

    const dataToSend = {
      nombre: form.nombre,
      apellido: form.apellido,
      correo: form.correo,
      rol_tienda_id: form.rol_tienda_id,
    };
    if (isEdit) dataToSend.estado = form.estado;

    setSaving(true);
    try {
      await onSubmit(dataToSend);
      onClose?.();
    } catch (err) {
      console.error("Error al guardar el usuario:", err);
      setAlert({ type: "error", message: getNiceErrorMessage(err, "No se pudo guardar el usuario.") });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="tienda-modal__overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="tienda-modal tienda-modal--shell" onMouseDown={(e) => e.stopPropagation()}>
        <div className="tienda-modal__header">
          <div className="tienda-modal__header-text">
            <h2 className="tienda-modal__title">{isEdit ? "Editar usuario" : "Invitar usuario"}</h2>
            <p className="tienda-modal__subtitle">
              {isEdit
                ? "Actualiza los datos y el rol del usuario."
                : "Se enviará una invitación por correo para activar la cuenta."}
            </p>
          </div>
          <button type="button" className="tienda-modal__close" onClick={onClose}>✕</button>
        </div>

        {alert && (
          <div className={`tienda-modal__alert tienda-modal__alert--${alert.type === "error" ? "error" : "info"}`}>
            <span>{alert.message}</span>
            <button type="button" className="tienda-modal__alert-x" onClick={() => setAlert(null)}>✕</button>
          </div>
        )}

        {!isEdit && (
          <div className="tienda-modal__info">
            Se enviará una invitación al correo para que el usuario configure su propia contraseña y active su cuenta.
          </div>
        )}

        <form onSubmit={submit} className="tienda-modal__body">
          <div className="tienda-modal__form-grid">
            <Field label="Nombre">
              <input name="nombre" value={form.nombre} onChange={updateField} required maxLength={100} className="tienda-modal__input" disabled={saving} />
            </Field>

            <Field label="Apellido">
              <input name="apellido" value={form.apellido} onChange={updateField} required maxLength={100} className="tienda-modal__input" disabled={saving} />
            </Field>

            <Field label="Correo" full>
              <input type="email" name="correo" value={form.correo} onChange={updateField} required maxLength={120} className="tienda-modal__input" disabled={saving} />
            </Field>

            <Field label="Rol" full={!isEdit}>
              <select name="rol_tienda_id" value={form.rol_tienda_id} onChange={updateField} required className="tienda-modal__select" disabled={saving}>
                <option value="">Selecciona un rol</option>
                {roles.map((r) => (
                  <option key={r.rol_tienda_id} value={r.rol_tienda_id}>
                    {r.nombre}
                  </option>
                ))}
              </select>
            </Field>

            {isEdit && (
              <Field label="Estado">
                <select name="estado" value={form.estado} onChange={updateField} className="tienda-modal__select" disabled={saving}>
                  <option value={1}>Activo</option>
                  <option value={0}>Inactivo</option>
                  <option value={3}>Bloqueado</option>
                  <option value={2} disabled>Invitación pendiente</option>
                </select>
              </Field>
            )}
          </div>

          <div className="tienda-modal__footer">
            <button type="button" className="tienda-modal__btn tienda-modal__btn--secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="tienda-modal__btn tienda-modal__btn--primary" disabled={saving}>
              {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Enviar invitación"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`tienda-modal__field ${full ? "tienda-modal__field--full" : ""}`}>
      <label className="tienda-modal__label">{label}</label>
      {children}
    </div>
  );
}

export default UserFormModal;
